import { z } from 'zod';

/**
 * Severity of a platform announcement.
 *
 * UPPERCASE on the wire — the backend persists `@Enumerated(EnumType.STRING)` and exposes the enum
 * NAME verbatim, with no mapping layer on either side (deliberate, so the two ends cannot drift).
 * Two backend tests pin this casing. Do NOT lowercase it here; derive CSS class names from it
 * instead.
 *
 * See docs/plans/2026-08-12-platform-announcements-pr1-implementation-report.md §4.2.
 */
export const ANNOUNCEMENT_SEVERITIES = ['INFO', 'WARNING', 'CRITICAL'] as const;

export const AnnouncementSeveritySchema = z.enum(ANNOUNCEMENT_SEVERITIES);

/**
 * What an announcement IS, as opposed to how loud it is — a SEPARATE axis from severity.
 *
 * Severity is a masking rank (see {@link ANNOUNCEMENT_SEVERITY_RANK}): a non-dismissible
 * `CRITICAL` hides everything below it for its whole window. A fourth severity for feature news
 * would inherit that and either mask a maintenance notice or be masked by one, and a customer
 * missing a planned-outage banner because we announced a new report is strictly worse than the
 * reverse. So:
 *
 * * `OPERATIONAL` — maintenance, incidents, deprecations. Owns the single banner slot.
 * * `FEATURE` — "this now exists". **Never renders in the banner**; it reaches the
 *   feature-discovery surface (the onboarding help menu) instead.
 *
 * UPPERCASE on the wire for the same reason as severity: the backend exposes the enum NAME
 * verbatim with no mapping layer on either side.
 */
export const ANNOUNCEMENT_KINDS = ['OPERATIONAL', 'FEATURE'] as const;

export const AnnouncementKindSchema = z.enum(ANNOUNCEMENT_KINDS);

/**
 * One announcement as returned by `GET /api/announcements/active`.
 *
 * This is the complete user-facing contract. It deliberately carries **no `createdBy` and no
 * targeting** — a regular user must not learn which staff member authored a banner or which other
 * organisations received it. Do not add fields the endpoint does not return; the admin projection
 * (`AnnouncementAdminResponse`, PR 3) is a different, super-admin-gated shape.
 *
 * `startsAt` / `endsAt` are UTC ISO-8601 strings (Kotlin `Instant`). They are parsed to epoch
 * milliseconds only at the point of comparison, never mutated into local time.
 */
export const AnnouncementSchema = z.object({
  id: z.string(),
  message: z.string(),
  severity: AnnouncementSeveritySchema,
  /**
   * Defaulted to `OPERATIONAL` for a response from a deployment that predates the axis (migration
   * 222). That is the historically correct reading — an operational banner is the only thing this
   * surface could express before — and it is also the SAFE direction: the alternative default
   * would silently route a maintenance notice away from the banner it was written for.
   */
  kind: AnnouncementKindSchema.default('OPERATIONAL'),
  dismissible: z.boolean(),
  startsAt: z.string(),
  endsAt: z.string(),
});

export const AnnouncementListSchema = z.array(AnnouncementSchema);

export type AnnouncementSeverity = z.infer<typeof AnnouncementSeveritySchema>;
export type AnnouncementKind = z.infer<typeof AnnouncementKindSchema>;
export type Announcement = z.infer<typeof AnnouncementSchema>;

/**
 * Display rank for the "show at most one banner" rule: CRITICAL beats WARNING beats INFO.
 *
 * A non-dismissible CRITICAL therefore masks every lower-severity announcement for its whole
 * window, with no user recourse. That is **intended** (plan OQ-8) — critical means critical — and is
 * pinned by a spec. Do not add a stacking exception.
 *
 * This rank is **unchanged** by the `kind` axis added on 2026-09-03 and applies only WITHIN the
 * operational banner slot. It is never applied across the two surfaces: a `FEATURE` announcement
 * cannot mask an operational one and cannot be masked by one, because it never enters this
 * comparison at all.
 */
export const ANNOUNCEMENT_SEVERITY_RANK: Readonly<Record<AnnouncementSeverity, number>> = {
  CRITICAL: 3,
  WARNING: 2,
  INFO: 1,
};
